import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  UserCheck, 
  Plus, 
  Loader2, 
  Trash2,
  Building2,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

export default function BeneficiariesPage() {
  const { api } = useAuth();
  
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    beneficiary_name: '',
    account_number: '',
    confirm_account_number: '',
    ifsc_code: '',
    bank_name: '',
    account_type: 'savings',
    nickname: ''
  });
  
  const [errors, setErrors] = useState({});

  const fetchBeneficiaries = useCallback(async () => {
    try {
      const response = await api.get('/beneficiaries/');
      setBeneficiaries(response.data || []);
    } catch (error) {
      console.error('Failed to fetch beneficiaries:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchBeneficiaries();
  }, [fetchBeneficiaries]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.beneficiary_name.trim()) {
      newErrors.beneficiary_name = 'Beneficiary name is required';
    }
    
    if (!formData.account_number.trim()) {
      newErrors.account_number = 'Account number is required';
    } else if (!/^\d{9,18}$/.test(formData.account_number)) {
      newErrors.account_number = 'Invalid account number (9-18 digits)';
    }
    
    if (formData.account_number !== formData.confirm_account_number) {
      newErrors.confirm_account_number = 'Account numbers do not match';
    }
    
    if (!formData.ifsc_code.trim()) {
      newErrors.ifsc_code = 'IFSC code is required';
    } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/i.test(formData.ifsc_code)) {
      newErrors.ifsc_code = 'Invalid IFSC code format';
    }
    
    if (!formData.bank_name.trim()) {
      newErrors.bank_name = 'Bank name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setSubmitting(true);
    
    try {
      await api.post('/beneficiaries/', {
        ...formData,
        ifsc_code: formData.ifsc_code.toUpperCase()
      });
      
      toast.success('Beneficiary added successfully');
      setShowAddDialog(false);
      setFormData({
        beneficiary_name: '',
        account_number: '',
        confirm_account_number: '',
        ifsc_code: '',
        bank_name: '',
        account_type: 'savings',
        nickname: ''
      });
      fetchBeneficiaries();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to add beneficiary';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/beneficiaries/${id}`);
      toast.success('Beneficiary removed');
      setBeneficiaries(beneficiaries.filter(b => b.id !== id));
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to remove beneficiary';
      toast.error(message);
    } finally {
      setDeleteId(null);
    }
  };

  // Bank list for autocomplete
  const popularBanks = [
    'HDFC Bank',
    'ICICI Bank',
    'State Bank of India',
    'Axis Bank',
    'Kotak Mahindra Bank',
    'Punjab National Bank',
    'Bank of Baroda',
    'Canara Bank',
    'IndusInd Bank',
    'Yes Bank',
    'IDFC First Bank',
    'Federal Bank',
    'RBL Bank',
    'South Indian Bank',
    'Bandhan Bank'
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold">Beneficiaries</h1>
          <p className="text-muted-foreground">Manage your saved bank accounts for payouts</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button data-testid="add-beneficiary-btn">
              <Plus className="mr-2 h-4 w-4" />
              Add Beneficiary
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add New Beneficiary</DialogTitle>
              <DialogDescription>
                Enter bank account details for payouts
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="beneficiary_name">Beneficiary Name *</Label>
                <Input
                  id="beneficiary_name"
                  placeholder="As per bank records"
                  value={formData.beneficiary_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, beneficiary_name: e.target.value }))}
                  className={errors.beneficiary_name ? 'border-red-500' : ''}
                />
                {errors.beneficiary_name && (
                  <p className="text-xs text-red-500">{errors.beneficiary_name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="account_number">Account Number *</Label>
                <Input
                  id="account_number"
                  placeholder="Enter account number"
                  value={formData.account_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, account_number: e.target.value.replace(/\D/g, '') }))}
                  className={errors.account_number ? 'border-red-500' : ''}
                />
                {errors.account_number && (
                  <p className="text-xs text-red-500">{errors.account_number}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm_account_number">Confirm Account Number *</Label>
                <Input
                  id="confirm_account_number"
                  placeholder="Re-enter account number"
                  value={formData.confirm_account_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, confirm_account_number: e.target.value.replace(/\D/g, '') }))}
                  className={errors.confirm_account_number ? 'border-red-500' : ''}
                />
                {errors.confirm_account_number && (
                  <p className="text-xs text-red-500">{errors.confirm_account_number}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="ifsc_code">IFSC Code *</Label>
                <Input
                  id="ifsc_code"
                  placeholder="e.g., HDFC0001234"
                  value={formData.ifsc_code}
                  onChange={(e) => setFormData(prev => ({ ...prev, ifsc_code: e.target.value.toUpperCase() }))}
                  className={errors.ifsc_code ? 'border-red-500' : ''}
                  maxLength={11}
                />
                {errors.ifsc_code && (
                  <p className="text-xs text-red-500">{errors.ifsc_code}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="bank_name">Bank Name *</Label>
                <Select 
                  value={formData.bank_name} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, bank_name: value }))}
                >
                  <SelectTrigger className={errors.bank_name ? 'border-red-500' : ''}>
                    <SelectValue placeholder="Select bank" />
                  </SelectTrigger>
                  <SelectContent>
                    {popularBanks.map(bank => (
                      <SelectItem key={bank} value={bank}>{bank}</SelectItem>
                    ))}
                    <SelectItem value="Other">Other Bank</SelectItem>
                  </SelectContent>
                </Select>
                {errors.bank_name && (
                  <p className="text-xs text-red-500">{errors.bank_name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="account_type">Account Type</Label>
                <Select 
                  value={formData.account_type} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, account_type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="savings">Savings</SelectItem>
                    <SelectItem value="current">Current</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="nickname">Nickname (Optional)</Label>
                <Input
                  id="nickname"
                  placeholder="e.g., Primary Account"
                  value={formData.nickname}
                  onChange={(e) => setFormData(prev => ({ ...prev, nickname: e.target.value }))}
                />
              </div>

              <DialogFooter className="pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} data-testid="submit-beneficiary-btn">
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    'Add Beneficiary'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Beneficiaries List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCheck className="w-5 h-5" />
            Saved Beneficiaries
          </CardTitle>
          <CardDescription>
            Bank accounts saved for quick payouts
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : beneficiaries.length === 0 ? (
            <div className="text-center py-12">
              <UserCheck className="w-12 h-12 mx-auto text-muted-foreground opacity-50 mb-4" />
              <p className="text-muted-foreground mb-4">No beneficiaries added yet</p>
              <Button onClick={() => setShowAddDialog(true)}>
                Add Your First Beneficiary
              </Button>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {beneficiaries.map((beneficiary) => (
                <div
                  key={beneficiary.id}
                  className="p-4 rounded-lg border bg-card hover:shadow-md transition-shadow"
                  data-testid={`beneficiary-card-${beneficiary.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold">{beneficiary.beneficiary_name}</p>
                        {beneficiary.nickname && (
                          <Badge variant="secondary" className="mt-1">{beneficiary.nickname}</Badge>
                        )}
                      </div>
                    </div>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="text-muted-foreground hover:text-red-500"
                          onClick={() => setDeleteId(beneficiary.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Remove Beneficiary</DialogTitle>
                          <DialogDescription>
                            Are you sure you want to remove {beneficiary.beneficiary_name}? This action cannot be undone.
                          </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setDeleteId(null)}>
                            Cancel
                          </Button>
                          <Button 
                            variant="destructive" 
                            onClick={() => handleDelete(beneficiary.id)}
                          >
                            Remove
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Bank</span>
                      <span className="font-medium">{beneficiary.bank_name}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Account</span>
                      <span className="font-mono">{beneficiary.account_number_masked}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">IFSC</span>
                      <span className="font-mono">{beneficiary.ifsc_code}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Type</span>
                      <span className="capitalize">{beneficiary.account_type}</span>
                    </div>
                  </div>
                  
                  <div className="mt-3 pt-3 border-t flex items-center justify-between">
                    <Badge variant={beneficiary.status === 'verified' ? 'default' : 'secondary'}>
                      {beneficiary.status === 'verified' ? (
                        <>
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Verified
                        </>
                      ) : (
                        <>
                          <AlertCircle className="w-3 h-3 mr-1" />
                          {beneficiary.status}
                        </>
                      )}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
